import React from "react";
import {
  Drawer,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";

import { ALERT_GLOSSARY } from "../../constants";
import {
  GlossaryContainer,
  GlossaryHeader,
  GlossaryIntro,
  GlossaryEntryBody,
  MetadataChips
} from "../../styles";

/** Props for the AlertGlossary drawer. */
interface AlertGlossaryProps {
  /** Whether the drawer is open. */
  open: boolean;
  /** Called when the user closes the drawer. */
  onClose: () => void;
}

/**
 * Right-side help drawer that documents every travel-analysis alert type,
 * regardless of which alerts are present in the current dashboard period.
 * Shows a short explanation of how the analysis is triggered, followed by
 * one collapsible entry per alert (description, trigger condition, scope and
 * the metadata it captures).
 */
const AlertGlossary: React.FC<AlertGlossaryProps> = ({ open, onClose }) => {

  const { t } = useTranslation();

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <GlossaryContainer>
        <GlossaryHeader>
          <div className="glossary-title">
            <HelpOutlineIcon />
            <span>{t(TravelAnalysisMessages.glossaryDrawerTitle)}</span>
          </div>
          <IconButton size="small" onClick={onClose} aria-label={t(TravelAnalysisMessages.close)}>
            <CloseIcon />
          </IconButton>
        </GlossaryHeader>

        <GlossaryIntro>
          <h3>{t(TravelAnalysisMessages.glossaryIntroTitle)}</h3>
          <p>{t(TravelAnalysisMessages.glossaryIntroText)}</p>
        </GlossaryIntro>

        {ALERT_GLOSSARY.map((entry) => (
          <Accordion key={entry.id} TransitionProps={{ unmountOnExit: true }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t(entry.titleKey)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <GlossaryEntryBody>
                <p>{t(entry.descriptionKey)}</p>
                <div className="entry-field">
                  <strong>{t(TravelAnalysisMessages.glossaryWhenLabel)}:</strong> {t(entry.whenKey)}
                </div>
                <div className="entry-field">
                  <strong>{t(TravelAnalysisMessages.glossaryAppliesLabel)}:</strong> {t(entry.appliesKey)}
                </div>
                <div className="entry-field">
                  <strong>{t(TravelAnalysisMessages.glossaryMetadataLabel)}:</strong>
                  <MetadataChips>
                    {entry.metadataKeys.map((metaKey) => (
                      <Chip key={metaKey} size="small" label={t(metaKey)} />
                    ))}
                  </MetadataChips>
                  <span className="meta-hint">{t(entry.metadataHintKey)}</span>
                </div>
              </GlossaryEntryBody>
            </AccordionDetails>
          </Accordion>
        ))}
      </GlossaryContainer>
    </Drawer>
  );
};

export default AlertGlossary;
